import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";

const VIS_KEY = "sh_visitor_id";
const CONV_KEY = "sh_chat_conv_id";
const NAME_KEY = "sh_chat_name";
const PHONE_KEY = "sh_chat_phone";

const getVisitorId = () => {
    let v = localStorage.getItem(VIS_KEY);
    if (!v) {
        v = "v_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
        localStorage.setItem(VIS_KEY, v);
    }
    return v;
};

export const ChatWidget = () => {
    const [open, setOpen] = useState(false);
    const [conversationId, setConversationId] = useState(() => localStorage.getItem(CONV_KEY) || null);
    const [name, setName] = useState(() => localStorage.getItem(NAME_KEY) || "");
    const [phone, setPhone] = useState(() => localStorage.getItem(PHONE_KEY) || "");
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [unreadBadge, setUnreadBadge] = useState(0);
    const endRef = useRef(null);
    const visitorId = useRef(getVisitorId());

    // Poll messages when we have a conversation
    useEffect(() => {
        if (!conversationId) return;
        let cancel = false;

        const fetchMessages = async (markRead) => {
            try {
                const { data } = await api.get(`/chat/${conversationId}/messages`, {
                    headers: { "X-Visitor-Id": visitorId.current },
                    params: { mark_read: markRead },
                });
                if (cancel) return;
                setMessages(data.messages);
                if (!open) {
                    setUnreadBadge(data.conversation.unread_for_customer || 0);
                } else {
                    setUnreadBadge(0);
                }
            } catch {
                // Conversation may have been deleted; reset
            }
        };

        fetchMessages(open);
        const interval = setInterval(() => fetchMessages(open), open ? 3000 : 8000);
        return () => { cancel = true; clearInterval(interval); };
    }, [conversationId, open]);

    useEffect(() => {
        if (open && endRef.current) endRef.current.scrollIntoView({ behavior: "smooth" });
    }, [messages, open]);

    const start = async (e) => {
        e.preventDefault();
        if (!name.trim() || !text.trim()) return;
        setSending(true);
        try {
            const { data } = await api.post("/chat/start", {
                visitor_id: visitorId.current,
                name: name.trim(),
                phone: phone.trim(),
                text: text.trim(),
            });
            localStorage.setItem(CONV_KEY, data.conversation_id);
            localStorage.setItem(NAME_KEY, name.trim());
            if (phone) localStorage.setItem(PHONE_KEY, phone.trim());
            setConversationId(data.conversation_id);
            setMessages([data.message]);
            setText("");
        } finally {
            setSending(false);
        }
    };

    const send = async (e) => {
        e.preventDefault();
        if (!text.trim() || sending) return;
        setSending(true);
        try {
            const { data } = await api.post(
                `/chat/${conversationId}/send`,
                { text: text.trim() },
                { headers: { "X-Visitor-Id": visitorId.current } }
            );
            setMessages((m) => [...m, data]);
            setText("");
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            <button
                data-testid="chat-toggle-btn"
                onClick={() => setOpen((o) => !o)}
                aria-label="Chat με το κατάστημα"
                className="fixed bottom-24 right-6 z-50 flex items-center gap-2 h-14 px-5 rounded-full bg-brand-red hover:bg-brand-redhover text-white shadow-2xl shadow-brand-red/40 transition hover:scale-105 active:scale-95"
            >
                {open ? <X size={20} /> : (
                    <>
                        <span className="relative flex h-2.5 w-2.5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-300 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400" />
                        </span>
                        <MessageCircle size={18} />
                        <span className="font-heading uppercase tracking-wider text-xs">Chat</span>
                    </>
                )}
                {!open && unreadBadge > 0 && (
                    <span data-testid="chat-unread-badge" className="absolute -top-1 -right-1 h-6 min-w-6 px-1 bg-brand-yellow text-black text-xs font-bold grid place-items-center rounded-full">
                        {unreadBadge}
                    </span>
                )}
            </button>

            {open && (
                <div
                    data-testid="chat-widget"
                    className="fixed bottom-40 right-4 sm:right-6 z-50 w-[calc(100%-2rem)] sm:w-96 h-[520px] max-h-[calc(100vh-11rem)] bg-[#16161a] border border-white/10 rounded-sm shadow-2xl flex flex-col overflow-hidden"
                >
                    <header className="bg-gradient-to-r from-brand-red to-brand-redhover p-4">
                        <div className="flex items-center gap-3">
                            <div className="relative h-10 w-10 rounded-full bg-white/10 grid place-items-center">
                                <MessageCircle size={18} />
                                <span className="absolute bottom-0 right-0 h-3 w-3 bg-emerald-400 rounded-full border-2 border-brand-red" />
                            </div>
                            <div>
                                <p className="font-heading uppercase text-sm">Grill Bros Support</p>
                                <p className="text-[11px] text-white/80">Συνήθως απαντάμε σε λίγα λεπτά</p>
                            </div>
                        </div>
                    </header>

                    {!conversationId ? (
                        <form onSubmit={start} className="flex-1 p-5 flex flex-col gap-3 bg-[#0c0c0e]" data-testid="chat-intake-form">
                            <p className="text-sm text-white/70">Πώς μπορούμε να βοηθήσουμε; Συμπλήρωσε για να ξεκινήσει η συνομιλία.</p>
                            <input
                                required data-testid="chat-name"
                                value={name} onChange={(e) => setName(e.target.value)}
                                placeholder="Όνομα *"
                                className="bg-[#16161a] border border-white/15 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-brand-red"
                            />
                            <input
                                data-testid="chat-phone"
                                value={phone} onChange={(e) => setPhone(e.target.value)}
                                placeholder="Τηλέφωνο (προαιρετικό)"
                                className="bg-[#16161a] border border-white/15 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-brand-red"
                            />
                            <textarea
                                required data-testid="chat-first-message"
                                value={text} onChange={(e) => setText(e.target.value)}
                                placeholder="Το μήνυμά σου..."
                                rows={3}
                                className="flex-1 bg-[#16161a] border border-white/15 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-brand-red resize-none"
                            />
                            <button type="submit" disabled={sending} data-testid="chat-start-btn" className="btn-primary text-sm flex items-center justify-center gap-2">
                                {sending ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
                                Στείλε
                            </button>
                        </form>
                    ) : (
                        <>
                            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#0c0c0e]" data-testid="chat-thread">
                                {messages.length === 0 ? (
                                    <p className="text-white/40 text-sm text-center py-8">Ξεκίνα τη συζήτηση!</p>
                                ) : messages.map((m) => (
                                    <div
                                        key={m.id}
                                        data-testid={`chat-msg-${m.sender}`}
                                        className={`max-w-[80%] px-3 py-2 rounded-lg text-sm leading-relaxed break-words ${
                                            m.sender === "customer"
                                                ? "ml-auto bg-brand-red text-white"
                                                : "bg-white/10 text-white"
                                        }`}
                                    >
                                        {m.text}
                                        <div className={`text-[10px] mt-1 ${m.sender === "customer" ? "text-white/70" : "text-white/40"}`}>
                                            {new Date(m.created_at).toLocaleTimeString("el-GR", { hour: "2-digit", minute: "2-digit" })}
                                        </div>
                                    </div>
                                ))}
                                <div ref={endRef} />
                            </div>
                            <form onSubmit={send} className="p-3 border-t border-white/10 flex gap-2 bg-[#16161a]">
                                <input
                                    data-testid="chat-input"
                                    value={text}
                                    onChange={(e) => setText(e.target.value)}
                                    placeholder="Γράψε μήνυμα..."
                                    className="flex-1 bg-[#0c0c0e] border border-white/15 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-brand-red"
                                />
                                <button type="submit" data-testid="chat-send-btn" disabled={sending || !text.trim()} className="h-9 w-9 bg-brand-red hover:bg-brand-redhover grid place-items-center rounded-sm disabled:opacity-50">
                                    {sending ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
                                </button>
                            </form>
                        </>
                    )}
                </div>
            )}
        </>
    );
};
